import { defineStore } from 'pinia'
import { sharedBackupPromiseCall } from '@shared/utils/async'
import { getAuthZApi } from '@/app/Services'
import { TC_MEMO } from '@/app/utils/constants'
import type { GrantAuthorizationWithDecodedAuthorization } from '@injectivelabs/sdk-ts'

type AuthZStoreState = {
  granterGrants: GrantAuthorizationWithDecodedAuthorization[]
  granteeGrants: GrantAuthorizationWithDecodedAuthorization[]
}
type RevokeAuthorizationParams = {
  grantee: string
  messageTypes: string[]
}

const initialStateFactory = (): AuthZStoreState => ({
  granterGrants: [],
  granteeGrants: []
})

export const useAuthZStore = defineStore('authZ', {
  state: (): AuthZStoreState => initialStateFactory(),
  getters: {
    hasGranteeGrants: (state: AuthZStoreState) => {
      return state.granteeGrants.length > 0
    },

    hasGranterGrants: (state: AuthZStoreState) => {
      return state.granterGrants.length > 0
    },

    hasGranterOrGranteeGrants: (state: AuthZStoreState) => {
      return state.granterGrants.length > 0 || state.granteeGrants.length > 0
    },

    grantersOrGranteesAddresses: (state: AuthZStoreState) => {
      return [
        ...new Set([
          ...state.granteeGrants.map((grant) => grant.granter),
          ...state.granterGrants.map((grant) => grant.grantee)
        ])
      ]
    },

    granterGrantsByAddress: (state) => {
      return Object.entries(
        state.granterGrants.reduce(
          (addressMap, grant) => {
            const address = grant.grantee
            const grants = addressMap[address] || []
            grants.push(grant)
            addressMap[address] = grants

            return addressMap
          },
          {} as Record<string, GrantAuthorizationWithDecodedAuthorization[]>
        )
      )
    },

    granteeGrantsByAddress: (state) => {
      return Object.entries(
        state.granteeGrants.reduce(
          (addressMap, grant) => {
            const address = grant.granter
            const grants = addressMap[address] || []
            grants.push(grant)
            addressMap[address] = grants

            return addressMap
          },
          {} as Record<string, GrantAuthorizationWithDecodedAuthorization[]>
        )
      )
    }
  },
  actions: {
    setGranterGrants(
      granterGrants: GrantAuthorizationWithDecodedAuthorization[]
    ) {
      this.granterGrants = granterGrants
    },

    setGranteeGrants(
      granteeGrants: GrantAuthorizationWithDecodedAuthorization[]
    ) {
      this.granteeGrants = granteeGrants
    },

    async fetchIfEmptyGrants() {
      const authZStore = useAuthZStore()

      if (authZStore.hasGranterOrGranteeGrants) {
        return
      }

      return authZStore.fetchGrants()
    },

    async fetchGrants() {
      const authZApi = await getAuthZApi()

      const authZStore = useAuthZStore()
      const sharedWalletStore = useSharedWalletStore()

      if (
        !sharedWalletStore.isUserConnected ||
        !sharedWalletStore.injectiveAddress
      ) {
        return
      }

      const pagination = { limit: 1000 }

      // We don't need to fetch the granteeGrants because there is no impersonate feature for now on TC
      //
      // const [{ grants: granteeGrants }, { grants: granterGrants }] =
      //   await Promise.all([
      //     authZApi.fetchGranteeGrants(
      //       sharedWalletStore.injectiveAddress,
      //       pagination
      //     ),
      //     authZApi.fetchGranterGrants(
      //       sharedWalletStore.injectiveAddress,
      //       pagination
      //     )
      //   ])

      const { grants: granterGrants } = await authZApi.fetchGranterGrants(
        sharedWalletStore.injectiveAddress,
        pagination
      )

      authZStore.setGranterGrants(
        granterGrants as GrantAuthorizationWithDecodedAuthorization[]
      )
      authZStore.setGranteeGrants(
        [] as GrantAuthorizationWithDecodedAuthorization[]
      )
    },

    async grantAuthorization({
      grantee,
      messageTypes
    }: {
      grantee: string
      messageTypes: string[]
    }) {
      const authZStore = useAuthZStore()
      const walletStore = useWalletStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      await walletStore.validateAndFundForGas()
      await sharedWalletStore.validateAndQueue()

      const { MsgGrant, getGenericAuthorizationFromMessageType } =
        await import('@injectivelabs/sdk-ts/core/modules')
      const messages = messageTypes.map((messageType) =>
        MsgGrant.fromJSON({
          grantee,
          authorization: getGenericAuthorizationFromMessageType(messageType),
          granter: sharedWalletStore.injectiveAddress
        })
      )

      await sharedWalletStore.broadcastFromMainWallet(
        messages,
        JSON.stringify(TC_MEMO)
      )

      await sharedBackupPromiseCall(() => authZStore.fetchGrants())
    },

    async revokeAuthorization({
      grantee,
      messageTypes
    }: RevokeAuthorizationParams) {
      const authZStore = useAuthZStore()

      await authZStore.revokeAuthorizations([{ grantee, messageTypes }])
    },

    async revokeAuthorizations(authorizations: RevokeAuthorizationParams[]) {
      const authZStore = useAuthZStore()
      const walletStore = useWalletStore()
      const sharedWalletStore = useSharedWalletStore()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      const { MsgRevoke } = await import('@injectivelabs/sdk-ts/core/modules')
      const messages = authorizations.flatMap(({ grantee, messageTypes }) =>
        messageTypes.map((messageType) =>
          MsgRevoke.fromJSON({
            grantee,
            messageType,
            granter: sharedWalletStore.injectiveAddress
          })
        )
      )

      if (messages.length === 0) {
        return
      }

      await walletStore.validateAndFundForGas()
      await sharedWalletStore.validateAndQueue()

      await sharedWalletStore.broadcastFromMainWallet(
        messages,
        JSON.stringify(TC_MEMO)
      )

      await sharedBackupPromiseCall(() => authZStore.fetchGrants())
    }
  }
})
